import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Users, Calendar, Clock, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/Button';
import { sendBokabordMail } from '../utils/infoMail';
interface BookTableModalProps {
  onClose: () => void;
}
type Step = 'meal-selection' | 'booking-form' | 'success';
type MealType = 'Frukost' | 'Fika' | 'Lunch' | 'AW';
const mealOptions: {
  type: MealType;
  description: string;
}[] = [{
  type: 'Frukost',
  description: 'Starta dagen på bästa sätt med nybakat.'
}, {
  type: 'Fika',
  description: 'Kaffe och något sött från vårt hantverksbageri.'
}, {
  type: 'Lunch',
  description: 'Vällagad mat från grunden.'
}, {
  type: 'AW',
  description: 'Avsluta dagen med god dryck och tilltugg.'
}];
export function BookTableModal({
  onClose
}: BookTableModalProps) {
  const [step, setStep] = useState<Step>('meal-selection');
  const [selectedMeal, setSelectedMeal] = useState<MealType | null>(null);
  // Form state
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [guests, setGuests] = useState(2);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const handleMealSelect = (meal: MealType) => {
    setSelectedMeal(meal);
    setStep('booking-form');
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    const emailSubject = `[BORDSBOKNING · ${selectedMeal}] ${date} kl ${time} – ${guests} pers – ${name}`;
    const emailMessage = [`════════════════════════════════`, `   NY BORDSBOKNING`, `   Gäller: Vanlig bordsbokning – ${selectedMeal}`, `════════════════════════════════`, '', `▸ DEL:        Bordsbokning (ordinarie servering)`, `▸ MÅLTID:     ${selectedMeal}`, `▸ DATUM:      ${date}`, `▸ TID:        ${time}`, `▸ GÄSTER:     ${guests} st`, '', `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, `   KONTAKTUPPGIFTER`, `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, `  Namn:     ${name}`, `  Telefon:  ${phone}`, `  E-post:   ${email}`, '', `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, `   ÖNSKEMÅL / MEDDELANDE`, `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, `  ${message || 'Inga särskilda önskemål'}`, '', `════════════════════════════════`, `Svara direkt på detta mejl för att nå gästen.`, `════════════════════════════════`].join('\n');
    try {
      const ok = await sendBokabordMail({
        subject: emailSubject,
        fromName: `Top Notch Bordsbokning – ${name}`,
        replyTo: email,
        name,
        message: emailMessage
      });
      if (!ok) throw new Error('Kunde inte skicka bokning');
      setStep('success');
    } catch (err) {
      setError('Något gick fel. Vänligen försök igen eller ring oss på 0470-52 36 03.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <AnimatePresence data-id="element-23" data-mp="uqsli1e12d2x">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto" onClick={onClose} data-id="element-24" data-mp="uq972unl66tn">
        <motion.div initial={{
        scale: 0.95,
        opacity: 0,
        y: 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.95,
        opacity: 0,
        y: 20
      }} onClick={e => e.stopPropagation()} className="bg-[#F2EDE4] rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden my-8" data-id="element-25" data-mp="18p8tdvnmm9mn">
          {/* Header */}
          <div className="bg-white px-4 md:px-6 py-3 md:py-4 border-b border-[#7C8062]/15 flex items-center justify-between sticky top-0 z-10" data-id="element-26" data-mp="2p4mrahvhg9ad">
            <div className="flex items-center gap-2 md:gap-3 min-w-0" data-id="element-27" data-mp="2yvr3j9trxg1i">
              {step === 'booking-form' && <button onClick={() => setStep('meal-selection')} className="flex items-center gap-1.5 px-3 py-2 -ml-2 text-sm font-medium text-[#7C8062] hover:text-[#1A1A1A] hover:bg-[#F2EDE4] rounded-lg transition-colors shrink-0" aria-label="Tillbaka" data-id="element-28" data-mp="2ty7quecq5bbo">
                  <ArrowLeft className="w-4 h-4" data-id="element-29" data-mp="2f6p4zzx6omke" />
                  <span data-id="element-30" data-mp="2b656arwypaw5">Tillbaka</span>
                </button>}
              <h2 className="text-lg md:text-xl font-serif text-[#1A1A1A] uppercase tracking-wide truncate" data-id="element-31" data-mp="3qnfdxqszrshp">
                Boka Bord
              </h2>
            </div>
            <button onClick={onClose} className="p-2 -mr-2 text-[#1A1A1A]/50 hover:text-[#1A1A1A] hover:bg-[#F2EDE4] rounded-full transition-colors shrink-0" aria-label="Stäng" data-id="element-32" data-mp="3mopqt6i5iwkv">
              <X className="w-5 h-5" data-id="element-33" data-mp="3k883qsije677" />
            </button>
          </div>

          <div className="p-6 md:p-8" data-id="element-34" data-mp="3p6t7h1wzz80e">
            {step === 'meal-selection' && <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 20
          }} className="space-y-6" data-id="element-35" data-mp="3p8mfoln9e0ai">
                <div className="space-y-2" data-id="element-36" data-mp="bygdrr7w53f2">
                  <h3 className="text-2xl font-serif text-[#1A1A1A]" data-id="element-37" data-mp="zq3u5f7j0w33">
                    Varmt välkommen att boka bord.
                  </h3>
                  <p className="text-[#1A1A1A]/70 text-sm" data-id="element-38" data-mp="14i55mywveis2">
                    Notera att vi har ett par platser för drop in-gäster.
                  </p>
                </div>

                <div className="space-y-3" data-id="element-39" data-mp="19eiy35c566nc">
                  {mealOptions.map(meal => <button key={meal.type} onClick={() => handleMealSelect(meal.type)} className="w-full text-left p-5 bg-white border border-[#7C8062]/20 rounded-xl hover:border-[#7C8062] hover:shadow-md transition-all group" data-id="element-40" data-mp="15b33sfnjoidt">
                      <h4 className="text-lg font-serif text-[#1A1A1A] mb-1 group-hover:text-[#7C8062] transition-colors" data-id="element-41" data-mp="qm6njvbc3z24">
                        {meal.type}
                      </h4>
                      <p className="text-sm text-[#1A1A1A]/60" data-id="element-42" data-mp="hnbgyi4domya">
                        {meal.description}
                      </p>
                    </button>)}
                </div>

                <div className="p-4 bg-[#7C8062]/5 rounded-xl border border-[#7C8062]/15 space-y-2" data-id="element-43" data-mp="7p5g83ub5mv0">
                  <p className="text-sm font-medium text-[#1A1A1A]" data-id="element-44" data-mp="2jaef44n8ivkb">
                    🏠 Vill du abonnera en del av eller hela lokalen?
                  </p>
                  <p className="text-xs text-[#1A1A1A]/70 leading-relaxed" data-id="element-45" data-mp="2t9wt84ggrn4g">
                    Mejla{' '}
                    <a href="mailto:bokabord@topnotchbakery.se?subject=Abonering" className="text-[#7C8062] font-semibold underline underline-offset-2 hover:text-[#6A6D53] transition-colors" data-id="element-46" data-mp="2xbqry773mh1z">
                      bokabord@topnotchbakery.se
                    </a>{' '}
                    med ändamålet i ämnesraden, t.ex.{' '}
                    <span className="italic text-[#1A1A1A]/90" data-id="element-47" data-mp="2ec4hlne1fivj">
                      "Abonering Frukost"
                    </span>
                    ,{' '}
                    <span className="italic text-[#1A1A1A]/90" data-id="element-48" data-mp="1hu88ljqon06t">
                      "Abonering AW"
                    </span>{' '}
                    osv. Skriv gärna i mejlet hur många ni är, vilket datum och
                    vilka tider det gäller.
                  </p>
                </div>

                <button onClick={onClose} className="w-full py-3 text-sm font-medium text-[#1A1A1A]/50 hover:text-[#1A1A1A] transition-colors" data-id="element-49" data-mp="abhe4ypfzn2u">
                  Stäng
                </button>
              </motion.div>}

            {step === 'booking-form' && <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} data-id="element-50" data-mp="t8ipd19lajf8">
                <div className="mb-6 pb-4 border-b border-[#7C8062]/15" data-id="element-51" data-mp="1gywg8ogfuc88">
                  <h3 className="text-xl font-serif text-[#1A1A1A] mb-1" data-id="element-52" data-mp="1cx1fbl2f60uz">
                    Boka {selectedMeal}
                  </h3>
                  <p className="text-sm text-[#1A1A1A]/60" data-id="element-53" data-mp="1vlblurmixy3n">
                    Fyll i dina uppgifter nedan för att slutföra bokningen.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5" data-id="element-54" data-mp="1mj2fmdyagqw2">
                  <div className="space-y-4" data-id="element-55" data-mp="1riyj8c32qgb3">
                    <div data-id="element-56" data-mp="1rhnfuddjjaki">
                      <label className="block text-xs font-medium text-[#1A1A1A]/70 mb-1.5" data-id="element-57" data-mp="1rl1htkialhrf">
                        Namn *
                      </label>
                      <input type="text" required value={name} onChange={e => setName(e.target.value)} className="w-full h-11 px-3 rounded-lg border border-[#7C8062]/20 bg-white focus:border-[#7C8062] focus:ring-2 focus:ring-[#7C8062]/20 outline-none transition-all text-sm" placeholder="För- och efternamn" data-id="element-58" data-mp="1hm2g6lvy2mdx" />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-59" data-mp="36xkis7v5t432">
                      <div data-id="element-60" data-mp="3b07fam2rzd6l">
                        <label className="block text-xs font-medium text-[#1A1A1A]/70 mb-1.5" data-id="element-61" data-mp="3b1ihwikhiew4">
                          Telefon *
                        </label>
                        <input type="tel" required value={phone} onChange={e => setPhone(e.target.value)} className="w-full h-11 px-3 rounded-lg border border-[#7C8062]/20 bg-white focus:border-[#7C8062] focus:ring-2 focus:ring-[#7C8062]/20 outline-none transition-all text-sm" placeholder="070-123 45 67" data-id="element-62" data-mp="31yr4id81t91u" />
                      </div>
                      <div data-id="element-63" data-mp="cha8uhdzqvw7">
                        <label className="block text-xs font-medium text-[#1A1A1A]/70 mb-1.5" data-id="element-64" data-mp="ch0gin5h4cnc">
                          E-post *
                        </label>
                        <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full h-11 px-3 rounded-lg border border-[#7C8062]/20 bg-white focus:border-[#7C8062] focus:ring-2 focus:ring-[#7C8062]/20 outline-none transition-all text-sm" placeholder="din@epost.se" data-id="element-65" data-mp="21ynkyw26z1pi" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-66" data-mp="1e3kwwqb7p2a3">
                      <div data-id="element-67" data-mp="1s22jgkpds8qf">
                        <label className="block text-xs font-medium text-[#1A1A1A]/70 mb-1.5" data-id="element-68" data-mp="1rz6kct1sre0e">
                          Antal gäster *
                        </label>
                        <div className="relative" data-id="element-69" data-mp="33f4znenkiwa2">
                          <input type="number" min="1" max="20" required value={guests} onChange={e => setGuests(parseInt(e.target.value) || 1)} className="w-full h-11 pl-10 pr-3 rounded-lg border border-[#7C8062]/20 bg-white focus:border-[#7C8062] focus:ring-2 focus:ring-[#7C8062]/20 outline-none transition-all text-sm" data-id="element-70" data-mp="2yhlj283ee7ey" />
                          <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1A1A]/40 pointer-events-none" data-id="element-71" data-mp="1hej4qijct0e9" />
                        </div>
                      </div>
                      <div data-id="element-72" data-mp="1r4khie69awdv">
                        <label className="block text-xs font-medium text-[#1A1A1A]/70 mb-1.5" data-id="element-73" data-mp="1r5vwla55jqka">
                          Datum *
                        </label>
                        <div className="relative" data-id="element-74" data-mp="yojkc8ygk9h6">
                          <input type="date" required value={date} onChange={e => setDate(e.target.value)} className="w-full h-11 pl-10 pr-3 rounded-lg border border-[#7C8062]/20 bg-white focus:border-[#7C8062] focus:ring-2 focus:ring-[#7C8062]/20 outline-none transition-all text-sm" data-id="element-75" data-mp="yn8e82hjipmw" />
                          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1A1A]/40 pointer-events-none" data-id="element-76" data-mp="85wcbkha2bva" />
                        </div>
                      </div>
                      <div data-id="element-77" data-mp="3ulqv9j7iv2tx">
                        <label className="block text-xs font-medium text-[#1A1A1A]/70 mb-1.5" data-id="element-78" data-mp="3ukzaz4jdkriy">
                          Tid *
                        </label>
                        <div className="relative" data-id="element-79" data-mp="3pktd36mii8sf">
                          <input type="time" required value={time} onChange={e => setTime(e.target.value)} className="w-full h-11 pl-10 pr-3 rounded-lg border border-[#7C8062]/20 bg-white focus:border-[#7C8062] focus:ring-2 focus:ring-[#7C8062]/20 outline-none transition-all text-sm" data-id="element-80" data-mp="3lk150gpdm9xq" />
                          <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1A1A]/40 pointer-events-none" data-id="element-81" data-mp="1fs4n6xcuthew" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-82" data-mp="15ughxdhmgrih">
                      <label className="block text-xs font-medium text-[#1A1A1A]/70 mb-1.5" data-id="element-83" data-mp="15uq8p3ahr2p8">
                        Önskemål / Meddelande
                      </label>
                      <textarea value={message} onChange={e => setMessage(e.target.value)} className="w-full min-h-[80px] p-3 rounded-lg border border-[#7C8062]/20 bg-white focus:border-[#7C8062] focus:ring-2 focus:ring-[#7C8062]/20 outline-none transition-all text-sm resize-y" placeholder="Allergier, barnvagn, eller andra önskemål..." data-id="element-84" data-mp="wsqqi7nqpskp" />
                    </div>
                  </div>

                  {error && <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-100" data-id="element-85" data-mp="3a539ystzbl3p">
                      {error}
                    </div>}

                  <div className="pt-4" data-id="element-86" data-mp="3k0n4baswabqc">
                    <Button type="submit" isLoading={isSubmitting} className="w-full py-3.5 text-base shadow-sm" data-id="element-87" data-mp="3k3spwg2bevhb">
                      Skicka bokningsförfrågan
                    </Button>
                    <p className="text-center text-xs text-[#1A1A1A]/50 mt-3" data-id="element-88" data-mp="3p1arfle068fa">
                      Vi återkommer med en bekräftelse så snart vi kan.
                    </p>
                  </div>
                </form>
              </motion.div>}

            {step === 'success' && <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="text-center py-8 space-y-4" data-id="element-89" data-mp="3tt2vzngl40wc">
                <div className="w-16 h-16 bg-[#7C8062]/10 rounded-full flex items-center justify-center mx-auto mb-2" data-id="element-90" data-mp="ckpomuc7dp50">
                  <CheckCircle2 className="w-8 h-8 text-[#7C8062]" data-id="element-91" data-mp="gj5fxafbhjo8" />
                </div>
                <h3 className="text-2xl font-serif text-[#1A1A1A]" data-id="element-92" data-mp="li028mchtqd3">
                  Tack för din förfrågan!
                </h3>
                <p className="text-[#1A1A1A]/70 max-w-sm mx-auto" data-id="element-93" data-mp="vafh3ashtytr">
                  Vi har tagit emot din bokningsförfrågan för {selectedMeal} den{' '}
                  {date} kl {time}. Vi återkommer till dig via e-post eller
                  telefon med en bekräftelse.
                </p>
                <div className="pt-6" data-id="element-94" data-mp="2osd4ry20lasq">
                  <Button onClick={onClose} className="w-full sm:w-auto px-8" data-id="element-95" data-mp="2otedvwzgzn7t">
                    Stäng
                  </Button>
                </div>
              </motion.div>}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>;
}